import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { eurf, Panel, tipStyle } from "./shared";

const PLANS = [["starter", "Starter"], ["pro", "Pro"], ["agence", "Agence"]];

export default function PlatformSettings({ token, readOnly }) {
  const [s, setS] = useState(null);
  const [hist, setHist] = useState([]);
  const [saving, setSaving] = useState(false);
  const [revPlans, setRevPlans] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const h = { headers: { Authorization: `Bearer ${token}` } };
  const loadRev = () => axios.get(`${API}/revolut/config`, h).then(({ data }) => setRevPlans(data.plans || {})).catch(() => {});
  useEffect(() => {
    axios.get(`${API}/superadmin/settings`, h).then(({ data }) => setS(data)).catch(() => {});
    axios.get(`${API}/superadmin/commission-history`, h).then(({ data }) => setHist(data)).catch(() => {});
    loadRev();
  }, []); // eslint-disable-line
  const syncRevolut = async () => {
    setSyncing(true);
    try {
      const { data } = await axios.post(`${API}/superadmin/revolut/plans/sync`, {}, h);
      toast.success(`${data.count} plan(s) Revolut synchronisé(s)`);
      loadRev();
    } catch (e) { toast.error(e.response?.data?.detail || "Échec de la synchronisation Revolut"); }
    finally { setSyncing(false); }
  };
  if (!s) return <p className="text-zinc-500" data-testid="sa-settings-loading">Chargement des paramètres…</p>;
  const feePct = parseFloat(s.stripe_fee_pct) || 0;
  const feeFixed = parseFloat(s.stripe_fee_fixed) || 0;
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/superadmin/settings`, {
        stripe_fee_pct: feePct, stripe_fee_fixed: feeFixed,
        halgaros_vat_rate: parseFloat(s.halgaros_vat_rate) || 0,
        halgaros_social_rate: parseFloat(s.halgaros_social_rate) || 0,
        halgaros_company: s.halgaros_company || "",
        halgaros_siret: s.halgaros_siret || "",
        halgaros_vat_number: s.halgaros_vat_number || "",
        halgaros_address: s.halgaros_address || "",
      }, h);
      setS(data);
      toast.success("Paramètres plateforme enregistrés");
    } catch { toast.error("Échec de l'enregistrement"); }
    finally { setSaving(false); }
  };
  return (
    <div className="space-y-4" data-testid="sa-settings">
      <Panel title="Frais Stripe & fiscalité HalgarOS">
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div><Label className="text-zinc-400">Frais Stripe (%)</Label><Input data-testid="sa-settings-stripe-pct" type="number" step="0.01" value={s.stripe_fee_pct} onChange={(e) => setS({ ...s, stripe_fee_pct: e.target.value })} className="mt-1 border-zinc-800 bg-zinc-900 text-white" /></div>
            <div><Label className="text-zinc-400">Frais Stripe fixe (€)</Label><Input data-testid="sa-settings-stripe-fixed" type="number" step="0.01" value={s.stripe_fee_fixed} onChange={(e) => setS({ ...s, stripe_fee_fixed: e.target.value })} className="mt-1 border-zinc-800 bg-zinc-900 text-white" /></div>
          </div>
          <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-3 text-xs text-zinc-500">
            <p className="mb-1 font-medium text-zinc-400">Frais Stripe Connect (référence)</p>
            Cartes UE/EEE : <b className="text-zinc-300">1,5 % + 0,25 €</b> · Royaume-Uni : 2,5 % + 0,25 € · Hors EEE : 3,25 % + 0,25 € · +2 % si conversion de devise. Frais Connect : 2 €/compte actif/mois + 0,25 % + 0,10 € par virement.
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div><Label className="text-zinc-400">TVA HalgarOS (%)</Label><Input data-testid="sa-settings-hg-vat" type="number" step="0.1" value={s.halgaros_vat_rate} onChange={(e) => setS({ ...s, halgaros_vat_rate: e.target.value })} className="mt-1 border-zinc-800 bg-zinc-900 text-white" /></div>
            <div><Label className="text-zinc-400">Charges HalgarOS (%)</Label><Input data-testid="sa-settings-hg-social" type="number" step="0.1" value={s.halgaros_social_rate} onChange={(e) => setS({ ...s, halgaros_social_rate: e.target.value })} className="mt-1 border-zinc-800 bg-zinc-900 text-white" /></div>
          </div>
          <p className="-mt-2 text-xs text-zinc-500">Utilisés dans la page Comptabilité pour estimer la TVA et les charges à déclarer sur vos abonnements. Les prix et commissions par plan se règlent dans <b className="text-zinc-400">Offres</b>.</p>
          <Button data-testid="sa-settings-save" onClick={save} disabled={saving || readOnly} className="rounded-full bg-[#FF6A00] font-semibold text-white hover:bg-[#E65F00]"><Check className="mr-1.5 h-4 w-4" />Enregistrer</Button>
        </div>
      </Panel>

      <Panel title="Infos légales vendeur (factures d'abonnement)">
        <div className="space-y-3">
          <p className="text-xs text-zinc-500">Ces informations apparaissent comme <b className="text-zinc-400">vendeur</b> sur les factures d'abonnement générées et envoyées chaque mois aux prestataires.</p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div><Label className="text-zinc-400">Raison sociale</Label><Input data-testid="sa-settings-company" value={s.halgaros_company || ""} onChange={(e) => setS({ ...s, halgaros_company: e.target.value })} className="mt-1 border-zinc-800 bg-zinc-900 text-white" placeholder="HalgarOS SAS" /></div>
            <div><Label className="text-zinc-400">SIRET</Label><Input data-testid="sa-settings-siret" value={s.halgaros_siret || ""} onChange={(e) => setS({ ...s, halgaros_siret: e.target.value })} className="mt-1 border-zinc-800 bg-zinc-900 text-white" /></div>
            <div><Label className="text-zinc-400">N° TVA</Label><Input data-testid="sa-settings-vatnum" value={s.halgaros_vat_number || ""} onChange={(e) => setS({ ...s, halgaros_vat_number: e.target.value })} className="mt-1 border-zinc-800 bg-zinc-900 text-white" placeholder="FR…" /></div>
            <div><Label className="text-zinc-400">Adresse</Label><Input data-testid="sa-settings-address" value={s.halgaros_address || ""} onChange={(e) => setS({ ...s, halgaros_address: e.target.value })} className="mt-1 border-zinc-800 bg-zinc-900 text-white" /></div>
          </div>
          <Button data-testid="sa-settings-legal-save" onClick={save} disabled={saving || readOnly} className="rounded-full bg-[#FF6A00] font-semibold text-white hover:bg-[#E65F00]"><Check className="mr-1.5 h-4 w-4" />Enregistrer</Button>
        </div>
      </Panel>

      <Panel title="Plans d'abonnement Revolut">
        <div className="space-y-3">
          <p className="text-xs text-zinc-500">Importe les plans (Starter / Pro / Agence) depuis Revolut vers cette base de données, sans rien recréer côté Revolut. À lancer après un déploiement en production si les plans n'apparaissent pas au moment de payer.</p>
          <div className="flex flex-wrap gap-2">
            {PLANS.map(([k, l]) => (
              <span key={k} data-testid={`sa-revplan-${k}`} className={`rounded-full border px-3 py-1 text-xs font-medium ${revPlans?.[k] ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-400" : "border-zinc-700 text-zinc-500"}`}>
                {l} {revPlans?.[k] ? "✓" : "—"}
              </span>
            ))}
          </div>
          <Button data-testid="sa-revplan-sync" onClick={syncRevolut} disabled={syncing || readOnly} className="rounded-full bg-[#FF6A00] font-semibold text-white hover:bg-[#E65F00]">
            {syncing ? "Synchronisation…" : "Synchroniser depuis Revolut"}
          </Button>
        </div>
      </Panel>

      <Panel title="Historique des commissions (6 derniers mois)">
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={hist} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#27272a" vertical={false} />
            <XAxis dataKey="month" tick={{ fill: "#a1a1aa", fontSize: 12 }} axisLine={{ stroke: "#3f3f46" }} tickLine={false} />
            <YAxis tick={{ fill: "#a1a1aa", fontSize: 12 }} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={tipStyle} cursor={{ fill: "#ffffff08" }} formatter={(v) => eurf(v)} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="commission" name="Ma commission (encaissée)" fill="#FF6A00" radius={[6, 6, 0, 0]} maxBarSize={40} />
            <Bar dataKey="stripe_fee" name="Frais Stripe (est.)" fill="#38bdf8" radius={[6, 6, 0, 0]} maxBarSize={40} />
          </BarChart>
        </ResponsiveContainer>
      </Panel>
    </div>
  );
}
